import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';

import { store } from 'redux/store';
import {
  selectDVCPMATrackingForecastSubpage,
  selectDVCPMATrackingForecastLocationId,
  selectDVCPMATrackingForecastMultiLocationIds,
  selectDVCPMATrackingForecastRounds,
  selectDVCPMATrackingForecastVariable,
  selectDVCPMATrackingForecastForecastDate,
  selectDVCPMATrackingForecastTimeFrequency,
  selectDVCPMATrackingForecastTriggerRequest,
  setTimeFrequency,
  setMultiGroupIds,
  setMultiLocationIds,
  setLocationId,
  setVariable,
  setForecastDate,
  setRounds,
  setAutoQuerying,
} from 'redux/reducers/meteorologyAgroTrackingForecastDetailedViewControlPanelReducer';
import {
  isPristine,
  resolveTimeFrequency,
  resolveVariable,
  mostRecentDate,
  mostRecentAvailableDate,
  allGroupIds,
  allLocationIds,
  firstLocationOfFirstGroup,
} from 'helper/Meteorology/Fields/TrackingForecast/DetailedView/openDefaults';

import useGroupedLocations from './useGroupedLocations';
import useTimeFrequency from './useTimeFrequency';
import useVariableOptions from './useVariableOptions';
import useForecastDateOptions from './useForecastDateOptions';
import useListPreferences from './useListPreferences';
import useSelectPreference from './useSelectPreference';

/**
 * Defaults ao abrir a Visão Detalhada (#1822, Área 3) — Agro.
 *
 * Espelha o hook do Fields (mesma cascata orientada a efeitos, mesmo latch
 * `autoFilling`), mas o Agro **não tem modelo nem membros** e só tem 2 modos:
 * Multi-Locais e Multi-Rodadas. Os resolvers puros são compartilhados com o
 * Fields — a decisão de default é a mesma nos dois domínios.
 */
export default function useApplyOpenDefaults() {
  const subpage = useSelector(selectDVCPMATrackingForecastSubpage);
  const locationId = useSelector(selectDVCPMATrackingForecastLocationId);
  const multilocationIds = useSelector(selectDVCPMATrackingForecastMultiLocationIds);
  const rounds = useSelector(selectDVCPMATrackingForecastRounds);
  const variable = useSelector(selectDVCPMATrackingForecastVariable);
  const forecastDate = useSelector(selectDVCPMATrackingForecastForecastDate);
  const timeFrequency = useSelector(selectDVCPMATrackingForecastTimeFrequency);
  const triggerRequest = useSelector(selectDVCPMATrackingForecastTriggerRequest);

  const { groups, locations } = useGroupedLocations();
  const { timeFrequencies } = useTimeFrequency();
  const { variables } = useVariableOptions();
  const { dates } = useForecastDateOptions();
  const { preferences, isLoading: preferencesLoading } = useListPreferences();
  const { selectPreference } = useSelectPreference();

  const [autoFilling, setAutoFilling] = useState(false);
  const decidedSubpage = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (decidedSubpage.current === subpage) return;
    if (preferencesLoading) return;
    decidedSubpage.current = subpage;

    const pristine = isPristine({
      locationId, multilocationIds, rounds, variable, forecastDate,
    }, subpage);
    if (!pristine) return;

    const [preference] = preferences;
    if (preference) {
      selectPreference(preference)();
      return;
    }

    setAutoFilling(true);
    // Sinaliza a auto-consulta em curso já no início da cascata: o gráfico mostra
    // o spinner durante todas as requisições em série, não só na consulta final
    // (#1822). Desliga no efeito do triggerRequest, junto com `autoFilling`.
    store.dispatch(setAutoQuerying(true));
  }, [
    subpage, preferencesLoading, preferences, selectPreference,
    locationId, multilocationIds, rounds, variable, forecastDate,
  ]);

  useEffect(() => {
    if (!autoFilling || timeFrequency !== undefined) return;
    const value = resolveTimeFrequency(timeFrequencies);
    if (value) store.dispatch(setTimeFrequency(value));
  }, [autoFilling, timeFrequency, timeFrequencies]);

  useEffect(() => {
    if (!autoFilling || !locations.length) return;

    if (subpage === 'locations') {
      if (multilocationIds.length) return;
      store.dispatch(setMultiGroupIds(allGroupIds(groups)));
      store.dispatch(setMultiLocationIds(allLocationIds(locations)));
      return;
    }

    if (locationId !== undefined) return;
    const location = firstLocationOfFirstGroup(groups, locations);
    if (location) store.dispatch(setLocationId(location.id));
  }, [autoFilling, subpage, groups, locations, multilocationIds, locationId]);

  useEffect(() => {
    if (!autoFilling || variable !== undefined) return;
    const value = resolveVariable(variables);
    if (value) store.dispatch(setVariable(value));
  }, [autoFilling, variable, variables]);

  useEffect(() => {
    if (!autoFilling) return;
    // Multi-Rodadas: a lista inclui a rodada de HOJE sem dados ainda — abrir nela
    // dava gráfico vazio (#1822). O default pula hoje pra data anterior; Multi-Locais
    // segue na mais recente (a lista dele não traz hoje sem dado).
    const date = subpage === 'rounds'
      ? mostRecentAvailableDate(dates, format(new Date(), 'yyyy-MM-dd'))
      : mostRecentDate(dates);
    if (!date) return;

    if (subpage === 'rounds') {
      if (rounds.length) return;
      store.dispatch(setRounds([date]));
      return;
    }

    if (forecastDate) return;
    store.dispatch(setForecastDate(date));
  }, [autoFilling, subpage, dates, rounds, forecastDate]);

  useEffect(() => {
    if (!autoFilling || !triggerRequest) return;
    triggerRequest();
    setAutoFilling(false);
    // Fim da cascata: a consulta final disparou e agora tem seu próprio isFetching.
    store.dispatch(setAutoQuerying(false));
  }, [autoFilling, triggerRequest]);
}
